import * as contentful from 'contentful'
import {
  ContentfulArticle,
  ContentfulReport,
  ContentfulNotice,
  ContentfulFeatured,
  ContentfulInvestor,
  Scope,
  ContentfulSeminar,
  ContentfulInsight,
  Insight,
  ContentfulEnterpriseNotice,
  ContentfulTagRanking,
  ContentfulNewTag,
  ContentfulGlobalNews,
  ContentfulNpStartupSlideArticleResponse,
  ContentfulNpStartupSlideArticle,
  ContentfulNpStartupSlideBannerAdResponse,
  ContentfulNpStartupStaticBanner,
  ContentfulNpStartupFloatingBanner,
  ContentfulNpStartupFloatingBannerAdResponse,
  ContentfulNpStartupFloatingArticleResponse,
  ContentfulNpStartupStaticBannerAdResponse,
  ContentfulNpStartupStaticArticleResponse,
} from '../../types'
import { Entry } from 'contentful'
import uniqWith from 'lodash/uniqWith'
import { convertArticle } from '../article'
import { dateInJST } from '../date'
import { isBrowser } from '../browser'
import { printError } from '../log'

const client = (() => {
  let client: contentful.ContentfulClientApi

  const [host, insecure] = isBrowser
    ? [process.env.CONTENTFUL_PROXY, false]
    : [process.env.CONTENTFUL_PROXY_SSR, true]

  return () => {
    if (!client) {
      client = contentful.createClient({
        space: process.env.CONTENTFUL_SPACE_ID!,
        accessToken: 'SECRET', // NOTE: Replaced by proxy
        environment: process.env.CONTENTFUL_ENVIRONMENT,
        host,
        insecure: process.env.CONTENTFUL_SPACE_ID != 'e2e' ? insecure : true,
      })
    }
    return client
  }
})()

const previewClient = (() => {
  let client: contentful.ContentfulClientApi

  return () => {
    if (!client) {
      client = contentful.createClient({
        space: process.env.CONTENTFUL_SPACE_ID!,
        accessToken: 'SECRET', // NOTE: Replaced by proxy
        environment: process.env.CONTENTFUL_ENVIRONMENT,
        host: process.env.CONTENTFUL_PREVIEW_PROXY,
        insecure: false,
      })
    }
    return client
  }
})()

export const getArticle = async (slugOrId: string) => {
  return (await getArticleBySlug(slugOrId)) || (await getArticleById(slugOrId))
}

export const getArticleBySlug = (slug: string) => {
  return client()
    .getEntries<ContentfulArticle>({
      include: 2,
      content_type: 'article',
      'fields.slug': slug,
    })
    .then((response) => {
      if (response.items.length > 0) {
        const entry = response.items[0]
        return { ...entry.fields, id: entry.sys.id }
      } else {
        return
      }
    })
    .catch((err) => {
      throw err
    })
}

export const getArticleById = (id: string) => {
  return client()
    .getEntries<ContentfulArticle>({ include: 2, 'sys.id': id })
    .then((response) => {
      if (response.items.length > 0) {
        const entry = response.items[0]
        return { ...entry.fields, id: entry.sys.id }
      } else {
        return
      }
    })
    .catch((err) => {
      throw err
    })
}

export const getPreviewArticle = async (slugOrId: string) => {
  return (
    (await getPreviewArticleBySlug(slugOrId)) ||
    (await getPreviewArticleById(slugOrId))
  )
}

export const getPreviewArticleBySlug = (slug: string) => {
  return previewClient()
    .getEntries<ContentfulArticle>({
      include: 2,
      content_type: 'article',
      'fields.slug': slug,
    })
    .then((response) => {
      if (response.items.length > 0) {
        const entry = response.items[0]
        return { ...entry.fields, id: entry.sys.id }
      } else {
        return
      }
    })
    .catch((err) => {
      throw err
    })
}

export const getPreviewArticleById = (id: string) => {
  return previewClient()
    .getEntries<ContentfulArticle>({ include: 2, 'sys.id': id })
    .then((response) => {
      if (response.items.length > 0) {
        const entry = response.items[0]
        return { ...entry.fields, id: entry.sys.id }
      } else {
        return
      }
    })
    .catch((err) => {
      throw err
    })
}

export const articlesPageLimit = 24

export const getArticles = (cursor: number = 0, limit?: number) => {
  const options = {
    'fields.publishedAt[lte]': dateInJST(),
    content_type: 'article',
    order: '-fields.publishedAt',
    skip: cursor,
    limit: limit || articlesPageLimit,
  }

  return client()
    .getEntries<ContentfulArticle>(options)
    .then((response) => ({
      items: response.items,
      total: response.total,
    }))
    .catch(printError)
}

export const getArticlesByKeyword = async (keyword: string) => {
  try {
    const [titleMatch, bodyMatch, introMatch] = await Promise.all([
      getAritlcesByKeywordOfField('title', keyword),
      getAritlcesByKeywordOfField('introduction', keyword),
      getAritlcesByKeywordOfField('body', keyword),
    ])

    const allMatchArticles = uniqWith(
      [...titleMatch.items, ...introMatch.items, ...bodyMatch.items],
      (a, b) => a.sys.id === b.sys.id
    )

    return {
      items: allMatchArticles,
      total: allMatchArticles.length,
    }
  } catch (e) {
    printError(e)
    return
  }
}

const getAritlcesByKeywordOfField = (field: string, keyword: string) => {
  const LIMIT = 3

  const options = {
    'fields.publishedAt[lte]': dateInJST(),
    content_type: 'article',
    limit: LIMIT,
    skip: 0,
    [`fields.${field}[match]`]: keyword,
  }

  return client()
    .getEntries<ContentfulArticle>(options)
    .then((response) => ({
      items: response.items,
      total: response.total,
    }))
    .catch((err) => {
      throw err
    })
}

export const getSeminar = (slug: string) => {
  return client()
    .getEntries<ContentfulSeminar>({
      content_type: 'seminar',
      'fields.slug': slug,
    })
    .then((response) => {
      if (response.items.length > 0) {
        const entry = response.items[0]
        return { ...entry.fields, id: entry.sys.id }
      } else {
        throw new Error(`Contentful: Not found slug="${slug}"`)
      }
    })
    .catch((err) => {
      throw err
    })
}

export const getPickupArticles = () => {
  return client()
    .getEntries({
      'fields.publishedAt[lte]': dateInJST(),
      content_type: 'pickup',
      order: '-fields.publishedAt,sys.id',
      limit: 1,
      include: 2,
    })
    .then((response) => {
      const articles =
        response.items.length > 0
          ? (response.items[0].fields as any).articles
          : []

      // Archiveされた記事を除外する
      return articles.filter((obj: { fields?: any }) =>
        obj.hasOwnProperty('fields')
      ) as contentful.Entry<ContentfulArticle>[]
    })
    .catch((err) => {
      throw err
    })
}

export const getSubPickups = () => {
  return client()
    .getEntries<ContentfulReport | ContentfulFeatured | ContentfulArticle>({
      'fields.publishedAt[lte]': dateInJST(),
      content_type: 'subPickup',
      order: '-fields.publishedAt,sys.id',
      limit: 1,
      include: 2,
    })
    .then((response) => {
      const items: (
        | contentful.Entry<ContentfulReport>
        | contentful.Entry<ContentfulFeatured>
        | contentful.Entry<ContentfulArticle>
      )[] =
        response.items.length > 0 ? (response.items[0].fields as any).items : []

      // Archiveされたホワイトペーパーを除外する
      return items.filter((obj: { fields?: any }) =>
        obj.hasOwnProperty('fields')
      )
    })
    .catch((err) => {
      throw err
    })
}

export const getNewPickups = () => {
  return client()
    .getEntries<ContentfulArticle>({
      content_type: 'newPickup',
      order: '-sys.createdAt',
      limit: 1,
    })
    .then((response) => {
      const articles = (response.items[0].fields as any).articles
      // Archiveされた記事を除外する
      return articles.filter((obj: { fields?: any }) =>
        obj.hasOwnProperty('fields')
      ) as contentful.Entry<ContentfulArticle>[]
    })
}

export const getGlobalFinanceNews = (limit: number) => {
  return client()
    .getEntries<ContentfulGlobalNews>({
      content_type: 'globalNews',
      order: '-fields.publishedAt',
      limit: limit,
    })
    .then((entries) => {
      return entries.items.map((item) => ({ ...item.fields }))
    })
}

// NOTE: 従来のお知らせと表示方法が異なるのでまずは1件のみ表示
const noticesPageLimit = 1

export const getNotices = (scopes: Scope[]) => {
  const now = dateInJST()
  return client()
    .getEntries<ContentfulNotice>({
      'fields.publishedAt[lte]': now,
      'fields.finishedAt[gt]': now,
      'fields.scope[in]': scopes.join(','),
      content_type: 'notice',
      order: '-fields.publishedAt',
      limit: noticesPageLimit,
    })
    .then((res) => res.items)
    .catch((err) => {
      throw err
    })
}

export const getNotice = async (slugOrId: string) => {
  return (await getNoticeBySlug(slugOrId)) || (await getNoticeById(slugOrId))
}

export const getNoticeById = (id: string) => {
  return client()
    .getEntry<ContentfulNotice>(id)
    .then((entry) => ({ ...entry.fields, id: entry.sys.id }))
    .catch((err) => {
      throw err
    })
}

export const getNoticeBySlug = (slug: string) => {
  return client()
    .getEntries<ContentfulNotice>({
      content_type: 'notice',
      'fields.slug': slug,
    })
    .then((response) => {
      if (response.items.length > 0) {
        const entry = response.items[0]
        return { ...entry.fields, id: entry.sys.id }
      } else {
        return
      }
    })
    .catch((err) => {
      throw err
    })
}

export const getEnterpriseNotices = () => {
  const now = dateInJST()
  return client()
    .getEntries<ContentfulEnterpriseNotice>({
      'fields.publishedAt[lte]': now,
      'fields.finishedAt[gt]': now,
      content_type: 'enterpriseNotice',
      order: '-fields.publishedAt',
    })
    .then((res) => res.items)
    .catch((err) => {
      throw err
    })
}

export const getInsight = (slug: string): Promise<Insight> => {
  return client()
    .getEntries<ContentfulInsight>({
      content_type: 'insight',
      'fields.slug': slug,
    })
    .then((response) => {
      if (response.items.length > 0) {
        const entry = response.items[0]
        const { relatedArticles: contenfulRelatedArticles, ...rest } =
          entry.fields

        const relatedArticles = contenfulRelatedArticles?.map((a) =>
          convertArticle({ ...a.fields, id: a.sys.id }, true)
        )
        return { ...rest, relatedArticles, id: entry.sys.id }
      } else {
        throw new Error(`Contentful: Not found slug="${slug}"`)
      }
    })
    .catch((err) => {
      throw err
    })
}

export const getArticlesByTag = (tagId: string) => {
  return client()
    .getEntries<ContentfulArticle>({
      'fields.publishedAt[lte]': dateInJST(),
      content_type: 'article',
      links_to_entry: tagId,
      order: '-fields.publishedAt',
    })
    .then((response) => ({
      items: response.items,
    }))
    .catch((err) => {
      throw err
    })
}

export const getArticlesByCompany = (
  companyId: string
): Promise<{ items: contentful.Entry<ContentfulArticle>[] }> => {
  return client()
    .getEntries<ContentfulArticle>({
      'fields.publishedAt[lte]': dateInJST(),
      content_type: 'article',
      links_to_entry: companyId,
      order: '-fields.publishedAt',
    })
    .then((response) => {
      return { items: response.items }
    })
    .catch((err) => {
      throw err
    })
}

export const getArticlesByInvestor = (investorId: string) => {
  return client()
    .getEntries<ContentfulArticle>({
      'fields.publishedAt[lte]': dateInJST(),
      content_type: 'article',
      links_to_entry: investorId,
      order: '-fields.publishedAt',
    })
    .then((response) => ({
      items: response.items,
    }))
    .catch((err) => {
      throw err
    })
}

export const getContentfulCompany = (shortname: string) => {
  return client()
    .getEntries<ContentfulInvestor>({
      content_type: 'company',
      'fields.shortname': shortname,
    })
    .then((response) => {
      const entry = response.items[0]
      return { ...entry.fields, id: entry.sys.id }
    })
    .catch((err) => {
      throw err
    })
}

export const getContentfulInvestor = (shortname: string) => {
  return client()
    .getEntries<ContentfulInvestor>({
      content_type: 'investor',
      'fields.shortname': shortname,
    })
    .then((response) => {
      const entry = response.items[0]
      return { ...entry.fields, id: entry.sys.id }
    })
    .catch((err) => {
      throw err
    })
}

export const getFeaturedByArticle = (articleId: string) => {
  return client()
    .getEntries<ContentfulFeatured>({
      content_type: 'featured',
      links_to_entry: articleId,
    })
    .then((response) => {
      const entry = response.items[0]
      return entry ? { ...entry.fields, id: entry.sys.id } : null
    })
    .catch((err) => {
      throw err
    })
}

export const getNewTag = (sfdcId: string) => {
  return client()
    .getEntries<ContentfulNewTag>({
      content_type: 'newTag',
      'fields.sfdcId': sfdcId,
    })
    .then((response) => {
      const entry = response.items[0]
      return { ...entry.fields, id: entry.sys.id }
    })
    .catch((err) => {
      throw err
    })
}

export const getTagRanking = () => {
  return client()
    .getEntries<ContentfulTagRanking>({
      content_type: 'tagRanking',
    })
    .then((response) => {
      const entry = response.items[0]
      return { ...entry.fields, id: entry.sys.id }
    })
    .catch((err) => {
      throw err
    })
}

export const getFeatured = (slug: string) => {
  return client()
    .getEntries<ContentfulFeatured>({
      content_type: 'featured',
      'fields.slug': slug,
      include: 2,
    })
    .then((response) => {
      const entry = response.items[0]
      return { ...entry.fields, id: entry.sys.id }
    })
    .catch((err) => {
      throw err
    })
}

export const getFeaturedList = () => {
  return client()
    .getEntries<ContentfulFeatured>({
      'fields.publishedAt[lte]': dateInJST(),
      content_type: 'featured',
      order: '-fields.publishedAt',
    })
    .then((res) =>
      res.items.map((item) => ({ ...item.fields, id: item.sys.id }))
    )
    .catch((err) => {
      throw err
    })
}

export const getNpStartupSlideBannerArticles = async (): Promise<
  ContentfulNpStartupSlideArticle[] | null
> => {
  const options = {
    content_type: 'npStartupMobileSlideBannerAd',
    order: '-sys.updatedAt',
    limit: 1,
  }
  return client()
    .getEntries<ContentfulNpStartupSlideBannerAdResponse>(options)
    .then((res) => {
      const articleMetas = res.items[0].fields.articles
      const articles: ContentfulNpStartupSlideArticle[] = articleMetas.map(
        (articleMeta: ContentfulNpStartupSlideArticleResponse) => {
          const article = articleMeta.fields
          return {
            articleUrl: article.articleUrl,
            articleBannerImageUrl:
              'https:' + article.articleBannerImageUrl.fields.file.url,
            newsId: article.newsId ? article.newsId : null,
          }
        }
      )
      return articles
    })
    .catch((err) => {
      printError(err)
      return null
    })
}

export const getNpStartupStaticBanner = async (): Promise<
  ContentfulNpStartupStaticBanner[] | null
> => {
  const options = {
    content_type: 'npStartupDesktopStaticBannerAd',
    order: '-sys.updatedAt',
    limit: 1,
  }
  return client()
    .getEntries<ContentfulNpStartupStaticBannerAdResponse>(options)
    .then((response) => {
      const articleMetas = response.items[0].fields.articles
      const articles: ContentfulNpStartupStaticBanner[] = articleMetas.map(
        (articleMeta: ContentfulNpStartupStaticArticleResponse) => {
          const article = articleMeta.fields
          return {
            articleUrl: article.articleUrl,
            articleBannerImageUrl:
              'https:' + article.articleBannerImageUrl.fields.file.url,
            newsId: article.newsId ? article.newsId : null,
          }
        }
      )
      return articles
    })
    .catch((err) => {
      printError(err)
      return null
    })
}

export const getNpStartupFloatingBanner = async (): Promise<
  ContentfulNpStartupFloatingBanner[] | null
> => {
  const options = {
    content_type: 'npStartupDesktopFloatingBannerAd',
    order: '-sys.updatedAt',
    limit: 1,
  }
  return client()
    .getEntries<ContentfulNpStartupFloatingBannerAdResponse>(options)
    .then((response) => {
      const articlesResponse = response.items[0].fields.articles
      const articles: ContentfulNpStartupFloatingBanner[] =
        articlesResponse.map(
          (articleResponse: ContentfulNpStartupFloatingArticleResponse) => {
            const article = articleResponse.fields
            return {
              articleUrl: article.articleUrl,
              articleBannerImageUrl:
                'https:' + article.articleBannerImageUrl.fields.file.url,
              newsId: article.newsId,
            }
          }
        )
      return articles
    })
    .catch((err) => {
      printError(err)
      return null
    })
}

export const notPublishedYet = (publishedAt: string) => {
  const now = dateInJST()
  const publishedDateTime = new Date(publishedAt)
  return now < publishedDateTime
}

export const getContentType = (item: contentful.Entry<any>) => {
  return item.sys.contentType?.sys.id
}

export const filterPublished = <T extends any>(
  entries: Entry<T | undefined>[]
) => entries.filter((entry) => entry.fields) as Entry<T>[]
